import { FC } from "react";

import { Translate } from "@/app/application/Translate";
import {
	TrainingFolder,
	TrainingFolderRequest,
} from "@/features/training/model/Training/TrainingFolder";
import { CustomCloseButton } from "@/features/training/ui";
import { CustomCloseIcon } from "@/features/training/ui";
import { CustomDeleteButton } from "@/features/training/ui";
import { CustomSaveButton } from "@/features/training/ui";
import CustomInput from "@/shared/forms/CustomInput";
import { AlertRequest } from "@/shared/notifications/AlertRequest";
import { AlertToast } from "@/shared/notifications/AlertToast";
import {
	Box,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import {
	useMutateDeleteTrainingFolder,
	useMutateUpdateTrainingFolder,
} from "@/service/TrainingMutations";

interface EditTrainingFolderProps {
	handleClose: () => void;
	open: boolean;
	folder: TrainingFolder;
}

const validationSchema = Yup.object({
	Name: Yup.string().trim().required("Name is required"),
});

export const EditTrainingFolder: FC<EditTrainingFolderProps> = (props) => {
	const updateTrainingFolder = useMutateUpdateTrainingFolder();
	const deleteTrainingFolder = useMutateDeleteTrainingFolder();

	const isEmptyFolder =
		(props.folder?.Links?.length ?? 0) === 0 &&
		(props.folder?.Courses?.length ?? 0) === 0;

	const handleSave = async (request: TrainingFolderRequest) => {
		try {
			await updateTrainingFolder({ request, folderId: props.folder.Id });
			props.handleClose();
			await AlertToast.success();
		} catch (error) {
			console.error(error);
			await AlertToast.failed();
		}
	};

	const handleDelete = async () => {
		try {
			props.handleClose();
			const confirmation = await AlertRequest.delete();
			if (confirmation.isConfirmed) {
				await deleteTrainingFolder(props.folder.Id);
				AlertRequest.deleteConfirmed("Folder deleted successfully.");
			}
		} catch (error) {
			console.error(error);
			AlertRequest.failed();
		}
	};

	return (
		<Dialog
			onClose={props.handleClose}
			open={props.open}
			fullWidth
			maxWidth="xs"
			aria-labelledby="edit-folder-title"
			PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
		>
			<DialogTitle
				id="edit-folder-title"
				sx={{ pr: 6, pb: 0.5, fontWeight: 700 }}
			>
				<Translate String="Training.EditFolder.Title">Edit Folder</Translate>
			</DialogTitle>

			<CustomCloseIcon onClick={props.handleClose} />

			<DialogContent dividers sx={{ pt: 1.5 }}>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					<Translate String="Training.EditFolder.Subtitle">
						Rename this folder or delete it if it’s empty.
					</Translate>
				</Typography>

				{/* Folder contents summary */}
				<Stack direction="row" spacing={1} sx={{ mb: 2 }}>
					<Chip
						size="small"
						variant="outlined"
						label={`${props.folder?.Courses?.length ?? 0} courses`}
					/>
					<Chip
						size="small"
						variant="outlined"
						label={`${props.folder?.Links?.length ?? 0} links`}
					/>
				</Stack>

				<Formik
					initialValues={{ Name: props.folder.Name }}
					validationSchema={validationSchema}
					validateOnMount
					enableReinitialize
					onSubmit={handleSave}
				>
					{({ isValid, dirty, isSubmitting }) => (
						<Form>
							<Stack spacing={2}>
								<CustomInput
									id="Name"
									type="text"
									label="Name"
									name="Name"
									translationString="Training.EditFolder.Name"
									placeholder="Folder name"
									autoFocus
									fullWidth
									required
									onKeyDown={(e) => e.stopPropagation()}
								/>

								<DialogActions
									sx={{
										px: 0,
										pt: 2,
										mt: 0.5,
										borderTop: (t) => `1px solid ${t.palette.divider}`,
										display: "flex",
										justifyContent: "space-between",
										gap: 1,
									}}
								>
									{/* Delete (left) */}
									<Box>
										<Tooltip
											title={
												isEmptyFolder
													? ""
													: "You can only delete an empty folder."
											}
											placement="top"
										>
											<span>
												<CustomDeleteButton
													handleDelete={async () => {
														await handleDelete();
													}}
													textOverride={
														<Translate String="Training.EditFolder.DeleteButton">
															Delete Folder
														</Translate>
													}
													sx={{
														borderRadius: 2,
														textTransform: "none",
														fontWeight: 800,
													}}
													loading={isSubmitting}
													disabled={!isEmptyFolder || isSubmitting}
												/>
											</span>
										</Tooltip>
									</Box>

									{/* Cancel / Save (right) */}
									<Box sx={{ display: "flex", gap: 1 }}>
										<CustomCloseButton
											handleClose={props.handleClose}
											disabled={isSubmitting}
										/>
										<CustomSaveButton
											loading={isSubmitting}
											disabled={!isValid || !dirty || isSubmitting}
										/>
									</Box>
								</DialogActions>
							</Stack>
						</Form>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};
